import { useState, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import ErrorBoundary from "@honeybadger-io/react";
import honeybadger from "../honeybadger.js";
import Button from "../components/Button.js";
import i18n from "../localization.js";
import { ModalHeader, useModal } from "../views/Modals.js";

import errorSrc from "../images/error.svg";
import offlineSrc from "../images/offline.svg";

const OurErrorBoundary = ({ children, inline = true }) => {
  return (
    <ErrorBoundary
      honeybadger={honeybadger}
      ErrorComponent={inline ? InlineErrorViewWrapper : ErrorViewWrapper}
    >
      <>{children}</>
    </ErrorBoundary>
  );
};

export default OurErrorBoundary;

const ErrorViewWrapper = (props) => <ErrorView {...props} inline={false} />;
const InlineErrorViewWrapper = (props) => (
  <ErrorView {...props} inline={true} />
);

export const ErrorView = ({
  error = null,
  info = null,
  errorOccurred,
  hideCloseButton = false,
  inline,
}) => {
  const modalRef = useModal();

  if (error) {
    console.log(error);
  }
  if (info) {
    console.log(info);
  }

  const errorMessages = {
    general: {
      title: i18n.t("ErrorView.generalErrorTitle"),
      message: i18n.t("ErrorView.generalErrorMessage"),
      image: errorSrc,
      imageWidth: 110,
    },
    outOfDateWeb: {
      title: i18n.t("ErrorView.applicationUpdatedTitle"),
      message: i18n.t("ErrorView.applicationUpdatedMessage"),
      image: errorSrc,
      imageWidth: 110,
    },
    outOfDateNative: {
      title: i18n.t("ErrorView.nativeOutOfDateTitle"),
      message: i18n.t("ErrorView.nativeOutOfDateMessage"),
      image: errorSrc,
      imageWidth: 110,
    },
    offline: {
      title: i18n.t("ErrorView.connectionOfflineTitle"),
      message: i18n.t("ErrorView.connectionOfflineMessage"),
      image: offlineSrc,
      imageWidth: 136,
    },
    gone: {
      title: i18n.t("ErrorView.goneTitle"),
      message: i18n.t("ErrorView.goneMessage"),
      image: errorSrc,
      imageWidth: 110,
    },
  };

  let errorMessage = errorMessages.general;
  let shouldShowRefreshButton = true;

  if (!navigator.onLine) {
    errorMessage = errorMessages.offline;
  } else if (error?.name === "ChunkLoadError") {
    errorMessage = errorMessages.outOfDateWeb;
  } else if (error?.status === 410) {
    errorMessage = errorMessages.gone;
  } else if (error?.status === 501 && error?.json?.name === "OldVersionError") {
    errorMessage =
      errorMessages[Capacitor.isNativePlatform() ? "outOfDateNative" : "outOfDateWeb"];
    if (Capacitor.isNativePlatform()) shouldShowRefreshButton = false;
  }

  return inline ? (
    <p style={{ color: "var(--red)" }}>
      <strong>{errorMessage.title}</strong>
    </p>
  ) : (
    <div className="dialog" style={{ padding: "var(--gap)" }}>
      {modalRef && !hideCloseButton && <ModalHeader showClose />}
      <img
        src={errorMessage.image}
        alt=""
        width={errorMessage.imageWidth}
        style={{ justifySelf: "center" }}
      />

      <div style={{ display: "grid", gridGap: "4px" }}>
        <h1 className="title">{errorMessage.title}</h1>
        <p className="large">{errorMessage.message}</p>
      </div>

      {shouldShowRefreshButton && <RefreshButton />}
    </div>
  );
};

export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);
  return isOnline;
};

export const OfflineGuard = ({ children }) => {
  const isOnline = useOnlineStatus();
  if (!isOnline) return <ErrorView inline={false} />;
  return <>{children}</>;
};

export const RefreshButton = () => {
  return (
    <Button
      variant="primary"
      onClick={() => window.location.replace(window.location.pathname)}
    >
      {i18n.t("ErrorView.refreshApplication")}
    </Button>
  );
};
